/**
 * AI authorship / provenance for tracks & releases.
 *
 * Philosophy (per Gordon): AI is just another tool in the box. This metadata is
 * informative and neutral — not a warning label. A real musician may use AI or
 * not; this just lets makers be transparent about how a piece was made.
 *
 * Stored as two flat fields on the work/release doc:
 *   aiLevel:   one of the 4 levels below
 *   aiPercent: rough % of the work that is AI-generated (0–100)
 */

export type AiLevel = 'human' | 'hybrid' | 'partly_ai' | 'ai';

export interface AiAuthorship {
  level: AiLevel;
  /** Rough % AI contribution, 0–100. */
  percent: number;
  compositionHuman?: boolean;
}

export const AI_LEVELS: {
  id: AiLevel;
  label: string;       // full label for forms
  short: string;       // badge label
  defaultPercent: number;
  badgeClass: string;
}[] = [
  { id: 'human',     label: '100% Human-made',          short: 'Human',        defaultPercent: 0,   badgeClass: 'bg-emerald-600/90 text-white border-emerald-500/40' },
  { id: 'hybrid',    label: 'AI-assisted (25% AI)',     short: '25% AI',       defaultPercent: 25,  badgeClass: 'bg-sky-600/90 text-white border-sky-500/40' },
  { id: 'partly_ai', label: '50% AI (Song: 100% Human)',short: '50% AI',       defaultPercent: 50,  badgeClass: 'bg-violet-600/90 text-white border-violet-500/40' },
  { id: 'ai',        label: '75%+ AI',                  short: '75% AI',       defaultPercent: 75,  badgeClass: 'bg-amber-500/90 text-black border-amber-400/40' },
];

export function aiLevelMeta(level?: string | null) {
  if (!level) return null;
  const l = level.toLowerCase();
  if (l === 'human') return AI_LEVELS[0];
  if (l === 'hybrid' || l === 'ai-assisted') return AI_LEVELS[1];
  if (l === 'partly_ai') return AI_LEVELS[2];
  if (l === 'ai' || l === 'ai-generated') return AI_LEVELS[3];
  return AI_LEVELS.find(item => item.id === l) || null;
}

/** Best-effort: derive a level from a bare percent if no explicit level was set. */
export function levelFromPercent(percent: number): AiLevel {
  if (percent <= 0) return 'human';
  if (percent >= 90) return 'ai';
  if (percent >= 60) return 'ai';
  if (percent >= 40) return 'partly_ai';
  return 'hybrid';
}

